/**
 * ChatAnnouncer — ARIA live-region infrastructure for the chat surface.
 *
 * Mounts a sr-only `aria-live` div.  Wave 3+ callers use the
 * `useChatAnnouncer()` hook to post messages into it.  Today no
 * callers exist — the infrastructure must be in place so Wave 3 can
 * wire "Response complete", error announcements, etc. without a
 * second structural PR.
 *
 * Architecture:
 *   <ChatAnnouncerProvider> mounts the live div and provides the
 *   `announce(msg, priority?)` imperative API via React Context.
 *   `useChatAnnouncer()` is the consumer hook.  Both are exported
 *   from this module.
 *
 * Usage:
 *   // Provider is mounted once in <ChatShell> — consumers can be
 *   // anywhere in the subtree.
 *   <ChatAnnouncerProvider>
 *     {children}
 *   </ChatAnnouncerProvider>
 *
 *   // Inside any descendant component:
 *   const { announce } = useChatAnnouncer();
 *   announce('Response complete');
 *   announce('Connection lost', 'assertive');
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §3.1 + §3.7
 */

import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';

// ── Context ──────────────────────────────────────────────────────────────────

export interface ChatAnnouncerContextValue {
  /**
   * Post a message to the ARIA live region.
   *
   * @param message   The text to announce to assistive technology.
   * @param priority  `'polite'` (default) waits for the user to be idle.
   *                  `'assertive'` interrupts immediately — use sparingly,
   *                  only for time-critical errors.
   */
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

const ChatAnnouncerContext = createContext<ChatAnnouncerContextValue | null>(
  null,
);

// ── Provider ─────────────────────────────────────────────────────────────────

interface ChatAnnouncerProviderProps {
  children: ReactNode;
}

/**
 * Mount once, high in the tree (inside `<ChatShell>`).
 * Renders two sr-only live regions — one polite, one assertive —
 * so `priority` switches can take effect immediately without needing
 * to clear a single region first.
 */
export function ChatAnnouncerProvider({
  children,
}: ChatAnnouncerProviderProps) {
  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);

  const announce = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      const el = priority === 'assertive' ? assertiveRef.current : politeRef.current;
      if (!el) return;
      // Clear then re-set forces screen-readers that cache the text to
      // announce again even when the message string is identical.
      el.textContent = '';
      // Use requestAnimationFrame so the DOM update and re-population
      // happen in separate paint cycles — required by some reader/browser
      // combinations to reliably trigger the announcement.
      requestAnimationFrame(() => {
        if (el) el.textContent = message;
      });
    },
    [],
  );

  return (
    <ChatAnnouncerContext.Provider value={{ announce }}>
      {children}
      {/* sr-only live regions — visually hidden but announced by screen-readers */}
      <div
        ref={politeRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="chat-announcer-polite"
      />
      <div
        ref={assertiveRef}
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        data-testid="chat-announcer-assertive"
      />
    </ChatAnnouncerContext.Provider>
  );
}

// ── Consumer hook ────────────────────────────────────────────────────────────

/**
 * Returns the `announce` function from the nearest
 * `<ChatAnnouncerProvider>`.
 *
 * Throws when called outside a provider (enforces correct usage).
 */
export function useChatAnnouncer(): ChatAnnouncerContextValue {
  const ctx = useContext(ChatAnnouncerContext);
  if (!ctx) {
    throw new Error(
      'useChatAnnouncer must be used inside a <ChatAnnouncerProvider>.',
    );
  }
  return ctx;
}
